import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { TrendUpIcon } from '@phosphor-icons/react'
import { useTheme } from 'styled-components'

import { ScrollableTabs } from '@reveni-io/reveni-ui'

import TimeRangeSelect from '@/common/components/TimeRangeSelect/TimeRangeSelect'
import TitlePage from '@/common/components/TitlePage/TitlePage'
import AppContext from '@/common/context/app.context'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'

import usePerformanceInsightsPeriod from './hooks/usePerformanceInsightsPeriod/usePerformanceInsightsPeriod'
import usePerformanceInsightsTab from './hooks/usePerformanceInsightsTab/usePerformanceInsightsTab'
import PerformanceInsightsContent from './PerformanceInsightsContent/PerformanceInsightsContent'
import PerformanceInsightsFooter from './PerformanceInsightsFooter/PerformanceInsightsFooter'
import { performanceInsightsPeriodOptions, performanceInsightsTabs } from './constants'
import { getPerformanceInsightsContentStatus, isSalesDataActivated } from './helpers'
import * as Styles from './styles'

const PerformanceInsightsPage = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { performanceInsightsTab, handleTabChange } = usePerformanceInsightsTab()
  const { returnPortalFeatures } = useReturnPortalFeatures({ returnPortalId: info.selectedReturnPortal?.id })
  const isAvailabilityEnabled = isSalesDataActivated(returnPortalFeatures)
  const {
    timeRange,
    startDate,
    endDate,
    minDate,
    maxDate,
    periodParams,
    availability,
    fetchingAvailability,
    availabilityError,
    handleTimeRangeChange,
    handleDateRangeChange,
  } = usePerformanceInsightsPeriod({ storeId: info.selectedStore?.id, isAvailabilityEnabled })

  const translatedTabs = performanceInsightsTabs.map(tab => ({ ...tab, label: t(tab.label) }))
  const contentStatus = getPerformanceInsightsContentStatus({
    returnPortalFeatures,
    periodParams,
    availability,
    fetchingAvailability,
    availabilityError,
  })

  return (
    <Styles.MainContainer>
      <Styles.HeaderContainer>
        <TitlePage Icon={TrendUpIcon}>{t('Performance Insights')}</TitlePage>
        <TimeRangeSelect
          key={`${startDate}-${endDate}`}
          options={performanceInsightsPeriodOptions}
          value={timeRange}
          onChange={handleTimeRangeChange}
          onChangeRanges={handleDateRangeChange}
          rangeValue={{ startDate, endDate }}
          minDate={minDate}
          maxDate={maxDate}
          backgroundColor={theme.colors.background.primary}
        />
      </Styles.HeaderContainer>
      <ScrollableTabs tabs={translatedTabs} value={performanceInsightsTab} onChange={handleTabChange} />
      <PerformanceInsightsContent status={contentStatus} periodParams={periodParams} />
      <PerformanceInsightsFooter />
    </Styles.MainContainer>
  )
}

export default PerformanceInsightsPage
